import { useEffect, useState } from "react";
import { Loader2, Save, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui-kit/Button";
import { Input, Label } from "@/components/ui-kit/Input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui-kit/Card";

const DEFAULT_NOTIFY = "kasten@lawspecialties.com";

function IntakeOptionsEditor() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => setForm({
      intake_show_calculator: r.data.intake_show_calculator == null ? true : !!r.data.intake_show_calculator,
      intake_notification_email: r.data.intake_notification_email || "",
    })).catch(() => setForm({ intake_show_calculator: true, intake_notification_email: "" }));
  }, []);

  const save = async () => {
    setSaving(true); setError("");
    try {
      await api.put("/settings", {
        intake_show_calculator: form.intake_show_calculator,
        intake_notification_email: form.intake_notification_email.trim(),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (form === null) return <div className="grid place-items-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4" data-testid="intake-options-editor">
      <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-secondary/30 p-3.5">
        <input
          type="checkbox"
          checked={form.intake_show_calculator}
          onChange={(e) => setForm({ ...form, intake_show_calculator: e.target.checked })}
          data-testid="intake-show-calculator-toggle"
          className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
        />
        <span className="text-sm text-foreground">
          Show the cost-estimate calculator on the welcome page
          <span className="mt-0.5 block text-xs text-muted-foreground">
            When on, clients can move a slider on <code className="rounded bg-secondary px-1">/welcome</code> to see how fees are
            shared. Turn it off to hide the estimate entirely.
          </span>
        </span>
      </label>

      <div className="max-w-md">
        <Label htmlFor="intake-notify">New-intake notification email</Label>
        <Input
          id="intake-notify"
          data-testid="intake-notification-email-input"
          value={form.intake_notification_email}
          onChange={(e) => setForm({ ...form, intake_notification_email: e.target.value })}
          placeholder={DEFAULT_NOTIFY}
        />
        <p className="mt-1.5 text-xs text-muted-foreground">
          Where the alert is sent when a client submits the welcome page. Leave blank to use the default
          (<code className="rounded bg-secondary px-1">{DEFAULT_NOTIFY}</code>).
        </p>
      </div>

      <div className="flex items-center gap-3">
        {saved && <span className="inline-flex items-center gap-1 text-sm text-status-paid"><Check className="h-4 w-4" /> Saved</span>}
        {error && <span className="text-sm text-destructive">{error}</span>}
        <div className="ml-auto">
          <Button size="sm" onClick={save} disabled={saving} data-testid="intake-options-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save options
          </Button>
        </div>
      </div>
    </div>
  );
}

export function IntakeOptionsCard() {
  return (
    <Card data-testid="intake-options-card">
      <CardHeader>
        <CardTitle>Welcome page options</CardTitle>
        <CardDescription>Control what the public <code>/welcome</code> onboarding page shows and where new-intake alerts are sent.</CardDescription>
      </CardHeader>
      <CardContent><IntakeOptionsEditor /></CardContent>
    </Card>
  );
}
